import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney, localDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    ArrowLeftIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    FolderArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';
import qs from 'qs';
import { useState } from 'react';
import AddressModal from './components/AddressModal/AddressModal';
import InvoiceIcon from './components/InvoiceIcon/InvoiceIcon';
import NoteModal from './components/NoteModal/NoteModal';

const tabs = [
    {
        id: 'revenue',
        name: 'Facturatie omzet',
    },
    {
        id: 'payments',
        name: 'Facturatie uitkeringen',
    },
];

const IndexView = () => {
    const [noteModal, setNoteModal] = useState(null);
    const [addressModal, setAddressModal] = useState(null);

    const { filters, updateFilters, isSorted } = useFilters([
        'invoices',
        'totals',
    ]);
    const { auth, invoices, resellers, years, statuses, months, totals } =
        usePage().props;

    let numberOfColumns = 10;

    if (auth.permissions?.includes('view customers')) {
        numberOfColumns += 1;
    }

    if (auth.permissions?.includes('download invoices')) {
        numberOfColumns += 1;
    }

    if (auth.permissions?.includes('update invoices')) {
        numberOfColumns += 2;
    }

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader header='Facturen'>
                    <Button variant='link' as={Link} href='/invoices/overview'>
                        <ArrowLeftIcon className='size-5' />
                        Terug
                    </Button>

                    <Button onClick={() => router.post('/invoices/sepa-batch')}>
                        Incasso-batch
                    </Button>
                    <Button
                        as={Link}
                        href={`/invoices/export?${qs.stringify({
                            year: filters.year,
                            month: filters?.month || null,
                            resellers: filters.resellers,
                            status: filters?.status || null,
                            tab: filters.tab,
                        })}`}
                        target='_blank'
                        rel='noreferrer noopener'
                    >
                        Exporteren
                    </Button>
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['invoices']}>
                        <FilterBarDropdown
                            label={`Resellers (${filters?.resellers?.length || 0}/${Object.keys(resellers || {})?.length || 0})`}
                            options={Object.entries(resellers || {})?.map(
                                ([id, name]) => ({ id, name })
                            )}
                            multiple
                            clearable
                            value={filters?.resellers || []}
                            onChange={values =>
                                updateFilters('resellers', values)
                            }
                        />
                        <FilterBarDropdown
                            label={
                                statuses?.find(s => s.id == filters?.status)
                                    ?.name || 'Alle statusen'
                            }
                            options={statuses || []}
                            clearable
                            value={filters?.status || ''}
                            onChange={values => updateFilters('status', values)}
                        />
                        <FilterBarDropdown
                            label={filters?.year || 'Jaar'}
                            options={years?.map(year => ({
                                id: year,
                                name: year,
                            }))}
                            value={filters?.year || ''}
                            onChange={values => updateFilters('year', values)}
                        />
                        <FilterBarDropdown
                            label={months?.[filters?.month] || 'Alle maanden'}
                            className='capitalize'
                            options={Object.entries(months || {})?.map(
                                ([id, name]) => ({ id, name })
                            )}
                            clearable
                            value={filters?.month || ''}
                            onChange={values => updateFilters('month', values)}
                        />
                        <FilterBarDropdown
                            label={
                                tabs?.find(tab => tab.id == filters?.tab)
                                    ?.name || 'Tab'
                            }
                            options={tabs || []}
                            value={filters?.tab || ''}
                            onChange={values => updateFilters('tab', values)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader id='table-header'>
                            <TableRow>
                                <TableHeaderCell />
                                <TableHeaderCell
                                    sorted={isSorted('number')}
                                    onClick={() =>
                                        updateFilters('sort', 'number')
                                    }
                                >
                                    Nr.
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('created_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'created_at')
                                    }
                                >
                                    Datum
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    BT
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('reminder_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'reminder_at')
                                    }
                                >
                                    Herinnering
                                </TableHeaderCell>
                                <TableHeaderCell>Reseller</TableHeaderCell>
                                {auth.permissions.includes(
                                    'view customers'
                                ) && (
                                    <TableHeaderCell textAlign='center'>
                                        Klantnr.
                                    </TableHeaderCell>
                                )}
                                <TableHeaderCell>Klant</TableHeaderCell>
                                <TableHeaderCell
                                    textAlign='right'
                                    sorted={isSorted('subtotal')}
                                    onClick={() =>
                                        updateFilters('sort', 'subtotal')
                                    }
                                >
                                    Totaal ex.
                                </TableHeaderCell>
                                <TableHeaderCell
                                    textAlign='right'
                                    sorted={isSorted('total')}
                                    onClick={() =>
                                        updateFilters('sort', 'total')
                                    }
                                >
                                    Totaal incl.
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('payed_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'payed_at')
                                    }
                                >
                                    Status
                                </TableHeaderCell>
                                {auth.permissions.includes(
                                    'download invoices'
                                ) && (
                                    <TableHeaderCell textAlign='center'>
                                        Bekijken
                                    </TableHeaderCell>
                                )}
                                {auth.permissions.includes(
                                    'update invoices'
                                ) && (
                                    <>
                                        <TableHeaderCell />
                                        <TableHeaderCell />
                                    </>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody id='table-body'>
                            {invoices?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={numberOfColumns} />
                            )}

                            {invoices.data?.map(invoice => (
                                <TableRow
                                    key={invoice.id}
                                    error={
                                        !invoice?.payed_at &&
                                        dayjs().diff(
                                            invoice?.created_at,
                                            'day'
                                        ) >=
                                            +invoice?.customer
                                                ?.payment_interval +
                                                16
                                    }
                                >
                                    <TableCell>
                                        <InvoiceIcon invoice={invoice} />
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            href={`/customers/${invoice.customer_id}/invoices/${invoice.id}`}
                                        >
                                            {invoice.number}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {localDate(invoice.created_at)}
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        {invoice.customer?.payment_interval}
                                    </TableCell>
                                    <TableCell>
                                        {localDate(invoice?.reminder_at)}
                                        {invoice.reminders > 1 &&
                                            ` (${invoice.reminders})`}
                                    </TableCell>
                                    <TableCell>
                                        {invoice?.customer?.reseller?.name}
                                    </TableCell>
                                    {auth.permissions.includes(
                                        'view customers'
                                    ) && (
                                        <TableCell textAlign='center'>
                                            <Link
                                                href={`/customers/${invoice.customer_id}/info`}
                                                target='_blank'
                                            >
                                                {invoice?.customer?.number}
                                            </Link>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Button
                                            variant='link'
                                            onClick={() =>
                                                setAddressModal(invoice)
                                            }
                                        >
                                            {invoice.customer?.name}
                                        </Button>
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {formatMoney(invoice.subtotal || 0)}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {formatMoney(invoice.total)}
                                    </TableCell>
                                    <TableCell>
                                        {invoice.status_name}
                                        <br />
                                        <small>
                                            {localDate(invoice.payed_at)}
                                        </small>
                                    </TableCell>
                                    {auth.permissions.includes(
                                        'download invoices'
                                    ) && (
                                        <TableCell textAlign='center'>
                                            <Link
                                                href={`/customers/${invoice.customer_id}/invoices/${invoice.id}/download`}
                                                target='_blank'
                                                rel='noreferrer noopener'
                                                className='inline-flex'
                                            >
                                                <FolderArrowDownIcon className='size-5' />
                                            </Link>
                                        </TableCell>
                                    )}
                                    {auth.permissions.includes(
                                        'update invoices'
                                    ) && (
                                        <>
                                            <TableCell textAlign='center'>
                                                <Button
                                                    variant='link'
                                                    onClick={() =>
                                                        setNoteModal(invoice)
                                                    }
                                                >
                                                    <DocumentIcon className='size-5' />
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                {invoice?.latest_action &&
                                                    auth.permissions?.includes(
                                                        'view notes'
                                                    ) && (
                                                        <Link
                                                            href={`/notes/${invoice?.latest_action?.id}`}
                                                        >
                                                            <ExclamationCircleIcon className='size-5 text-orange-600' />
                                                        </Link>
                                                    )}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter id='table-footer'>
                            <TableRow>
                                <TableCell colSpan={6} />
                                {auth.permissions?.includes(
                                    'view customers'
                                ) && <TableCell />}
                                <TableCell textAlign='right'>Totaal:</TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(totals?.subtotal || 0)}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(totals?.total || 0)}
                                </TableCell>
                                <TableCell />
                                {auth.permissions.includes(
                                    'download invoices'
                                ) && <TableCell />}
                                {auth.permissions.includes(
                                    'update invoices'
                                ) && <TableCell colSpan={2} />}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={invoices?.meta}
                        only={['invoices']}
                    />
                </SegmentFooter>
            </Segment>

            {noteModal?.id && (
                <NoteModal
                    invoice={noteModal}
                    open={!!noteModal?.id}
                    onClose={() => setNoteModal(null)}
                />
            )}

            {addressModal?.id && (
                <AddressModal
                    invoice={addressModal}
                    open={!!addressModal?.id}
                    onClose={() => setAddressModal(null)}
                />
            )}
        </Container>
    );
};

export default IndexView;
